// Vehicle tyre layouts (from GVW transport table + common Indian fleet types).
// Each layout describes axles with tyre counts (2 = single per side, 4 = dual per side).
// Position codes follow the convention F{n}L/R or R{n}L/R (single), and
// F{n}LO/LI/RO/RI or R{n}LO/LI/RO/RI for dual (Outer/Inner).

const single = (axleId, label) => ({
    axle: axleId,
    label,
    tyres: [
        { code: `${axleId}L`, side: "L", inner: false },
        { code: `${axleId}R`, side: "R", inner: false },
    ],
});

const dual = (axleId, label) => ({
    axle: axleId,
    label,
    tyres: [
        { code: `${axleId}LO`, side: "L", inner: false },
        { code: `${axleId}LI`, side: "L", inner: true },
        { code: `${axleId}RI`, side: "R", inner: true },
        { code: `${axleId}RO`, side: "R", inner: false },
    ],
});

// Axle pattern from Excel: front list + rear list, each entry = 2 (single) or 4 (dual).
function build(frontAxles, rearAxles) {
    const out = [];
    frontAxles.forEach((n, i) => {
        const id = `F${i + 1}`;
        out.push(n === 4 ? dual(id, `Front Axle ${i + 1}`) : single(id, `Front Axle ${i + 1}`));
    });
    rearAxles.forEach((n, i) => {
        const id = `R${i + 1}`;
        out.push(n === 4 ? dual(id, `Rear Axle ${i + 1}`) : single(id, `Rear Axle ${i + 1}`));
    });
    return out;
}

// Stepney / spare tyre — every road-going vehicle on the fleet is expected
// to carry at least one. We attach it as a layout-level `spare` entry rather
// than another axle so the renderer can draw it off-chassis with a clear
// SPARE badge. Position code `SP` is treated as any other tyre position by
// the backend (`position: str`), so no API change is required.
const WITH_SPARE = (layout) => ({ ...layout, spare: { code: "SP" } });

export const VEHICLE_LAYOUTS = {
    motorcycle_2:        WITH_SPARE({ label: "Motorcycle (2)",                  tyres: 2,  gvw: null,  axles: [single("F1", "Front"), single("R1", "Rear")] }),
    auto_3:              WITH_SPARE({ label: "Auto / 3-wheeler (3)",            tyres: 3,  gvw: null,  axles: [{ axle: "F1", label: "Front", tyres: [{ code: "F1C", side: "C", inner: false }] }, single("R1", "Rear")] }),
    car_4:               WITH_SPARE({ label: "Car / 4-wheeler (4)",             tyres: 4,  gvw: null,  axles: build([2], [2]) }),
    suv_4:               WITH_SPARE({ label: "SUV / Pickup (4)",                tyres: 4,  gvw: 3500,  axles: build([2], [2]) }),
    bus_6:               WITH_SPARE({ label: "Bus (6)",                         tyres: 6,  gvw: 16200, axles: build([2], [4]) }),
    tractor_4:           WITH_SPARE({ label: "Tractor (4)",                     tyres: 4,  gvw: null,  axles: build([2], [2]) }),
    truck_6:             WITH_SPARE({ label: "6-tyre truck",                    tyres: 6,  gvw: 19000, axles: build([2], [4]) }),
    truck_10_multi_axle: WITH_SPARE({ label: "10-tyre multi-axle truck",        tyres: 10, gvw: 28500, axles: build([2], [4, 4]) }),
    truck_12_rigid_a:    WITH_SPARE({ label: "12-tyre rigid truck (2-2|4-4)",   tyres: 12, gvw: 36000, axles: build([2, 2], [4, 4]) }),
    truck_12_rigid_b:    WITH_SPARE({ label: "12-tyre rigid truck (2|2-4-4)",   tyres: 12, gvw: 36000, axles: build([2], [2, 4, 4]) }),
    truck_14_rigid_a:    WITH_SPARE({ label: "14-tyre rigid truck (2-2|2-4-4)", tyres: 14, gvw: 43500, axles: build([2, 2], [2, 4, 4]) }),
    truck_14_rigid_b:    WITH_SPARE({ label: "14-tyre rigid truck (2-2|4-4-2)", tyres: 14, gvw: 43500, axles: build([2, 2], [4, 4, 2]) }),
    truck_16_rigid:      WITH_SPARE({ label: "16-tyre rigid truck",             tyres: 16, gvw: 49000, axles: build([2, 2], [4, 4, 4]) }),
    semi_14:             WITH_SPARE({ label: "14-tyre semi trailer",            tyres: 14, gvw: 40000, axles: build([2, 4], [4, 4]) }),
    semi_18_a:           WITH_SPARE({ label: "18-tyre semi trailer (2-4|4-4-4)", tyres: 18, gvw: 46000, axles: build([2, 4], [4, 4, 4]) }),
    semi_18_b:           WITH_SPARE({ label: "18-tyre semi trailer (2-4-4|4-4)", tyres: 18, gvw: 49500, axles: build([2, 4, 4], [4, 4]) }),
    semi_22:             WITH_SPARE({ label: "22-tyre semi trailer",            tyres: 22, gvw: 55000, axles: build([2, 4, 4], [4, 4, 4]) }),
};

export const VEHICLE_TYPE_OPTIONS = Object.entries(VEHICLE_LAYOUTS).map(([key, v]) => ({
    value: key,
    label: `${v.label}${v.gvw ? ` · ${v.gvw.toLocaleString()} kg` : ""}`,
}));

export function getLayout(code) {
    return VEHICLE_LAYOUTS[code] || null;
}

export function getAllPositions(code) {
    const l = getLayout(code);
    if (!l) return [];
    const axlePositions = l.axles.flatMap((a) => a.tyres.map((t) => t.code));
    return l.spare ? [...axlePositions, l.spare.code] : axlePositions;
}
